'use client';

import { c as _c } from "react/compiler-runtime";
import { useFeatures } from '@udemy-v2/react-experimentation';
import { useInstructorCommunityBannerInfoByInstructorIdQuery } from '@/generated/graphql';

/**
 * Feature code gating the instructor community badges/ribbon/banner.
 * Monolith: ExpPlatform.get_feature_variant("instructor_community")
 */
export const FEATURE_INSTRUCTOR_COMMUNITY = 'instructor_community';

/**
 * Reads the community id off the banner info payload as a number.
 *
 * GraphQL types the id as `ID` (a string) while the event schema declares a `long`, so it is
 * converted here. Anything that is not a number yields `undefined`, and callers skip the event
 * rather than publish a bogus id.
 */
export function readInstructorCommunityId(bannerInfo: {
  id?: string | number | null;
} | null | undefined): number | undefined {
  const id = bannerInfo?.id;
  if (id == null || id === '') {
    return undefined;
  }
  const numericId = Number(id);
  return Number.isFinite(numericId) ? numericId : undefined;
}

/**
 * Fetches an instructor's published community banner info via GraphQL
 * (instructorCommunityBannerInfoByInstructorId), gated behind the
 * instructor_community experiment. Returns null/false when the experiment
 * is disabled for the current viewer, or when the instructor has no
 * published community.
 */
export function useInstructorCommunityBannerInfo(instructorId) {
  const $ = _c(15);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [FEATURE_INSTRUCTOR_COMMUNITY];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    featureVariantAssignments
  } = useFeatures(t0);
  let t1;
  if ($[1] !== featureVariantAssignments) {
    t1 = featureVariantAssignments.get(FEATURE_INSTRUCTOR_COMMUNITY)?.configuration?.enabled;
    $[1] = featureVariantAssignments;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const isExperimentEnabled = t1 === true;
  const t2 = String(instructorId);
  let t3;
  if ($[3] !== t2) {
    t3 = {
      instructorId: t2
    };
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const t4 = instructorId != null && isExperimentEnabled;
  let t5;
  if ($[5] !== t4) {
    t5 = {
      enabled: t4
    };
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const query = useInstructorCommunityBannerInfoByInstructorIdQuery(t3, t5);
  const bannerInfo = isExperimentEnabled ? query.data?.instructorCommunityBannerInfoByInstructorId ?? null : null;
  let t6;
  if ($[7] !== bannerInfo) {
    t6 = readInstructorCommunityId(bannerInfo);
    $[7] = bannerInfo;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const t7 = isExperimentEnabled && bannerInfo != null;
  const t8 = isExperimentEnabled && query.isLoading;
  const t9 = (query.error ?? undefined) as Error | undefined;
  let t10;
  if ($[9] !== bannerInfo || $[10] !== t6 || $[11] !== t7 || $[12] !== t8 || $[13] !== t9) {
    t10 = {
      bannerInfo,
      communityId: t6,
      hasCommunity: t7,
      isLoading: t8,
      error: t9
    };
    $[9] = bannerInfo;
    $[10] = t6;
    $[11] = t7;
    $[12] = t8;
    $[13] = t9;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  return t10;
}